"use client"

import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { X, Share, SquarePlus, Zap, Smartphone, ArrowRight } from "lucide-react"

// Mobile-only "get the app" banner + install bottom sheet.
//
// BOOTH isn't in the App Store / Play Store — it installs as a PWA
// straight from the browser (manifest.json already ships standalone
// display). This banner teaches that. Renders as a white strip INSIDE
// the mobile landing header (PublicNavbar) so it reads as one seamless
// white block with the nav and shares its hide-on-scroll motion.
//
// Visibility rules:
//   • mobile only (parent renders it md:hidden via the landing header)
//   • hidden when already installed (display-mode: standalone)
//   • hidden after dismiss (X) — persisted to localStorage
//
// Cross-component sync: LandingModeToggle sits fixed at top-[69px]
// under the 56px nav. When this banner is visible the header grows, so
// we announce visibility via a "booth:install-banner-change"
// CustomEvent — same idiom as "booth:landing-mode-change".

const DISMISS_KEY = "boothInstallBannerDismissed"

export const INSTALL_BANNER_EVENT = "booth:install-banner-change"

type Platform = "ios" | "android" | "other"

function detectPlatform(): Platform {
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/.test(ua)) return "ios"
  if (/Android/.test(ua)) return "android"
  return "other"
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari legacy flag
    (navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

export function InstallAppBanner() {
  const [visible, setVisible] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [platform, setPlatform] = useState<Platform>("other")
  // Android Chrome hands us a real install prompt via beforeinstallprompt;
  // when captured we show a one-tap Install button instead of steps.
  const [installPrompt, setInstallPrompt] = useState<{ prompt: () => Promise<void> } | null>(null)

  useEffect(() => {
    try {
      if (isStandalone() || window.localStorage.getItem(DISMISS_KEY) === "1") return
    } catch {}
    setPlatform(detectPlatform())
    setVisible(true)

    const onBeforeInstall = (e: Event) => {
      e.preventDefault()
      setInstallPrompt(e as unknown as { prompt: () => Promise<void> })
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstall)
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall)
  }, [])

  // Announce visibility so fixed chrome below the header (the
  // Barber/Owner toggle) can shift down in sync.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent(INSTALL_BANNER_EVENT, { detail: visible }))
  }, [visible])

  // Lock page scroll while the sheet is up.
  useEffect(() => {
    if (!sheetOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [sheetOpen])

  const dismiss = () => {
    setVisible(false)
    setSheetOpen(false)
    try {
      window.localStorage.setItem(DISMISS_KEY, "1")
    } catch {}
  }

  const handleInstallTap = async () => {
    if (installPrompt) {
      // Native Android prompt — no instructions needed.
      await installPrompt.prompt()
      return
    }
    setSheetOpen(true)
  }

  if (!visible) return null

  return (
    <>
      {/* Banner strip — white, black text, hairline divider from the nav
          row beneath so the block stays seamless. Compact: small type,
          text-link CTA (no pill) so it reads as chrome, not a card. */}
      <div className="flex items-center gap-3 px-4 py-1.5 border-b border-gray-100">
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-semibold text-black leading-tight">Download BOOTH on your phone</p>
          <p className="text-[10px] text-gray-500 leading-tight mt-0.5">
            Add it to your home screen. No App Store needed
          </p>
        </div>
        <button
          onClick={handleInstallTap}
          className="flex-shrink-0 inline-flex items-center gap-1 text-[12px] font-medium text-black"
        >
          See how
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button onClick={dismiss} aria-label="Dismiss" className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Install bottom sheet — slides over the page, never navigates,
          so iOS users can reach the Share button mid-instructions.
          Portaled to <body>: the parent header carries a transform (its
          slide-away animation), which would otherwise hijack fixed
          positioning and pin this sheet to the header instead of the
          viewport. */}
      {sheetOpen &&
        createPortal(
        <div className="fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-black/50 animate-in fade-in duration-200"
            onClick={() => setSheetOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl px-6 pt-3 pb-8 animate-in slide-in-from-bottom duration-300">
            {/* Grab handle */}
            <div className="mx-auto w-10 h-1 rounded-full bg-gray-200 mb-5" />

            <h2 className="text-xl font-semibold text-black tracking-tight">BOOTH, Download Now.</h2>
            <p className="mt-1.5 text-sm text-gray-500 leading-relaxed">
              We&apos;re not in the App Store or Google Play. Get BOOTH&apos;s app from browser to your home screen.
            </p>

            {/* Benefits */}
            <div className="mt-5 space-y-3">
              <div className="flex items-center gap-3">
                <Zap className="w-4 h-4 text-black flex-shrink-0" />
                <p className="text-sm text-gray-700">Opens instantly from your home screen</p>
              </div>
              <div className="flex items-center gap-3">
                <Smartphone className="w-4 h-4 text-black flex-shrink-0" />
                <p className="text-sm text-gray-700">Full app download, no browser needed</p>
              </div>
            </div>

            {/* Device-specific install steps */}
            <div className="mt-6 rounded-2xl bg-gray-50 p-4">
              {platform === "ios" ? (
                <>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">iPhone (Safari)</p>
                  {/* Step text is normal inline flow (icons use inline +
                      align-[-3px]) so lines wrap like a sentence instead
                      of flex-stacking words around the icons. */}
                  <ol className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 mt-px rounded-full bg-black text-white text-[11px] font-semibold flex items-center justify-center">
                        1
                      </span>
                      <span className="text-sm text-gray-800 leading-snug">
                        Tap the share button <Share className="w-4 h-4 inline align-[-3px]" /> at the bottom
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 mt-px rounded-full bg-black text-white text-[11px] font-semibold flex items-center justify-center">
                        2
                      </span>
                      <span className="text-sm text-gray-800 leading-snug">
                        Scroll down and tap <SquarePlus className="w-4 h-4 inline align-[-3px]" /> “Add to Home
                        Screen”
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 mt-px rounded-full bg-black text-white text-[11px] font-semibold flex items-center justify-center">
                        3
                      </span>
                      <span className="text-sm text-gray-800 leading-snug">Tap “Add”</span>
                    </li>
                  </ol>
                  <p className="mt-4 text-sm text-gray-500">Use BOOTH like any other app.</p>
                </>
              ) : platform === "android" ? (
                <>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Android (Chrome)</p>
                  <ol className="space-y-3">
                    <li className="flex items-center gap-3 text-sm text-gray-800">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-black text-white text-[11px] font-semibold flex items-center justify-center">
                        1
                      </span>
                      Tap the menu (⋮) in Chrome
                    </li>
                    <li className="flex items-center gap-3 text-sm text-gray-800">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-black text-white text-[11px] font-semibold flex items-center justify-center">
                        2
                      </span>
                      Tap “Add to Home screen”
                    </li>
                    <li className="flex items-center gap-3 text-sm text-gray-800">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-black text-white text-[11px] font-semibold flex items-center justify-center">
                        3
                      </span>
                      Tap “Install”
                    </li>
                  </ol>
                  <p className="mt-4 text-sm text-gray-500">Use BOOTH like any other app.</p>
                </>
              ) : (
                <p className="text-sm text-gray-700">
                  Open <span className="font-medium text-black">boothapp.co</span> on your phone&apos;s browser and
                  you&apos;ll see install steps right here.
                </p>
              )}
            </div>

            <button
              onClick={() => setSheetOpen(false)}
              className="mt-5 w-full inline-flex items-center justify-center h-11 text-sm font-medium rounded-full bg-black text-white hover:bg-zinc-900 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
